import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Badge, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { cn } from '@/lib/cn'
import { NoAccess, StaffGate, isForbidden } from '@/lib/staffGate'

import { formatMoney } from '@/lib/money'

import {
  useCount,
  useMovements,
  useRecordMovement,
  useStock,
  useThreshold,
  useUnitCost,
} from '../api'
import type { ItemKind, Movement, StockGroup, StockLine } from '../types'

/**
 * L'écran de réserve.
 *
 * Il ne sert pas à consulter le stock — pour ça, il y a le poste de vente, qui
 * grise ce qui manque. Il sert à **saisir ce qui vient de se passer** : une
 * livraison arrive, un carton tombe, on recompte avant le service.
 *
 * D'où l'ordre : ce qui manque d'abord, la saisie à portée de pouce sur la
 * ligne concernée, et le journal accessible en un geste — parce que la
 * question qui se pose vraiment n'est jamais « combien en reste-t-il » mais
 * « où sont passées les vingt qui manquent ».
 *
 * Le comptage se saisit comme on compte : on tape ce qu'on a dans les mains.
 * C'est le serveur qui en déduit l'écart, ce qui laisse survivre une vente
 * passée pendant qu'on comptait.
 */
export function InventoryPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('inventory.title')}>
      {(session, signOut) => <InventoryBoard email={session.user.email} onSignOut={signOut} />}
    </StaffGate>
  )
}

/**
 * Le tableau lui-même, séparé de l'écran qui l'ouvre.
 *
 * Deux portes mènent au même stock : la réserve, où l'on se connecte pour
 * saisir une livraison, et le back-office, où l'administration vient regarder
 * ce qui reste et ce que ça vaut. Les dupliquer aurait produit deux écrans
 * qui divergent au premier correctif ; l'en-tête de session est donc
 * facultatif, et c'est la seule chose qui les distingue.
 */
export function InventoryBoard({
  email,
  onSignOut,
}: {
  email?: string
  onSignOut?: () => void
}) {
  const { t, i18n } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id

  const [alertsOnly, setAlertsOnly] = useState(false)
  const [openRow, setOpenRow] = useState<string | null>(null)

  const stock = useStock(eventId, alertsOnly)

  useEffect(() => {
    // Le back-office pose son propre titre : le tableau ne le lui vole pas.
    if (email) document.title = t('inventory.title')
  }, [t, email])

  // Un refus n'est pas une réserve vide.
  if (isForbidden(stock.error)) {
    return <NoAccess title={t('inventory.title')} onSignOut={onSignOut} />
  }

  if (eventQuery.isPending || stock.isPending) {
    return (
      <Container className="py-16">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Container>
    )
  }

  const data = stock.data
  const lines = data?.lines ?? []

  return (
    <Container className={cn(email ? 'py-8' : 'px-0 py-0')}>
      {email && onSignOut ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* La langue à côté du nom de l'écran : le coin « compte » garde
              sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Overline>{t('inventory.title')}</Overline>
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-3 text-sm text-content-muted">
            <span>{email}</span>
            <Button variant="ghost" size="sm" onClick={onSignOut}>
              {t('checkin.logout')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {data && data.out_count > 0 ? (
          <Badge tone="ember">{t('inventory.outCount', { count: data.out_count })}</Badge>
        ) : null}
        {data && data.low_count > 0 ? (
          <Badge tone="champagne">{t('inventory.lowCount', { count: data.low_count })}</Badge>
        ) : null}
        {data && data.out_count === 0 && data.low_count === 0 ? (
          <Badge tone="success">{t('inventory.allGood')}</Badge>
        ) : null}

        <Button
          variant={alertsOnly ? 'outline' : 'ghost'}
          size="sm"
          onClick={() => setAlertsOnly((current) => !current)}
        >
          {t('inventory.alertsOnly')}
        </Button>
      </div>

      {/* Ce que la reserve vaut. La question se pose une fois par semaine avant
          la soiree, et tous les jours apres : c'est de l'argent immobilise sur
          des etageres, et personne ne le voyait nulle part. */}
      {data?.value_visible ? (
        <Card className="mt-6">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('inventory.totalValue')}
          </p>
          <p className="mt-1 font-display text-heading-lg tabular-nums text-content-primary">
            {formatMoney(
              { amount_minor: data.total_value_minor, currency: data.currency },
              i18n.language,
            )}
          </p>
          {/* Un produit sans prix d'achat ne vaut pas zero : il manque au
              total. Le dire vaut mieux qu'afficher une reserve sous-evaluee de
              moitie sans que personne s'en apercoive. */}
          {data.unpriced_count > 0 ? (
            <p className="mt-2 text-caption text-champagne-300">
              {t('inventory.unpriced', { count: data.unpriced_count })}
            </p>
          ) : null}
        </Card>
      ) : null}

      {lines.length === 0 ? (
        <p className="mt-8 text-sm text-content-muted">
          {alertsOnly ? t('inventory.nothingLow') : t('inventory.nothingTracked')}
        </p>
      ) : null}

      {/* Une reserve se compte par rayon, pas produit par produit. */}
      <div className="mt-6 space-y-8">
        {(data?.groups ?? []).map((group) => (
          <GroupBlock
            key={group.key}
            group={group}
            currency={data?.currency ?? 'EUR'}
            showValue={data?.value_visible ?? false}
            eventId={eventId}
            defaultThreshold={data?.default_threshold ?? 0}
            openRow={openRow}
            onToggle={(refId) =>
              setOpenRow((current) => (current === refId ? null : refId))
            }
          />
        ))}
      </div>
    </Container>
  )
}

/**
 * Un rayon de la reserve.
 *
 * Le titre porte ce qu'on vient verifier d'un coup d'oeil : combien d'unites,
 * combien ca vaut, combien de lignes reclament une attention. Le detail est en
 * dessous, pour qui a une question precise a poser a une bouteille.
 */
function GroupBlock({
  group,
  currency,
  showValue,
  eventId,
  defaultThreshold,
  openRow,
  onToggle,
}: {
  group: StockGroup
  currency: string
  showValue: boolean
  eventId: string | undefined
  defaultThreshold: number
  openRow: string | null
  onToggle: (refId: string) => void
}) {
  const { t, i18n } = useTranslation()

  const title =
    group.label ||
    t(group.family === 'pack' ? 'inventory.groupPacks' : 'inventory.groupUncategorised')

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-line-subtle pb-2">
        <h2 className="font-display text-heading-sm text-content-primary">
          {title}
          {group.alerts > 0 ? (
            <span className="ml-2 align-middle">
              <Badge tone="champagne">{group.alerts}</Badge>
            </span>
          ) : null}
        </h2>
        <p className="text-caption tabular-nums text-content-muted">
          {t('inventory.groupUnits', { count: group.remaining })}
          {showValue
            ? ` \u00b7 ${formatMoney({ amount_minor: group.value_minor, currency }, i18n.language)}`
            : ''}
          {showValue && group.unpriced > 0
            ? ` \u00b7 ${t('inventory.unpricedShort', { count: group.unpriced })}`
            : ''}
        </p>
      </div>

      <div className="mt-3 space-y-3">
        {group.lines.map((line) => (
          <StockRow
            key={`${line.item_type}:${line.ref_id}`}
            line={line}
            currency={currency}
            showValue={showValue}
            eventId={eventId}
            defaultThreshold={defaultThreshold}
            isOpen={openRow === line.ref_id}
            onToggle={() => onToggle(line.ref_id)}
          />
        ))}
      </div>
    </section>
  )
}

function StockRow({
  line,
  currency,
  showValue,
  eventId,
  defaultThreshold,
  isOpen,
  onToggle,
}: {
  line: StockLine
  currency: string
  showValue: boolean
  eventId: string | undefined
  defaultThreshold: number
  isOpen: boolean
  onToggle: () => void
}) {
  const { t, i18n } = useTranslation()

  return (
    <Card
      className={cn(
        'border-l-4',
        line.is_out
          ? 'border-l-ember-400'
          : line.is_low
            ? 'border-l-champagne-400'
            : 'border-l-transparent',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-wrap items-baseline justify-between gap-3 text-left"
        aria-expanded={isOpen}
      >
        <span className="min-w-0">
          <span className="block truncate text-content-primary">{line.label}</span>
          <span className="mt-1 block text-caption text-content-muted">
            {t('inventory.received', { count: line.stock_quantity })} ·{' '}
            {t('inventory.sold', { count: line.sold_count })}
            {line.held > 0 ? ` · ${t('inventory.held', { count: line.held })}` : ''}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span
            className={cn(
              'font-display text-heading-lg tabular-nums',
              line.is_out
                ? 'text-ember-300'
                : line.is_low
                  ? 'text-champagne-300'
                  : 'text-content-primary',
            )}
          >
            {line.remaining}
          </span>
          <span className="block text-caption text-content-muted">
            {t('inventory.thresholdAt', { count: line.threshold })}
          </span>
          {/* La valeur de ce qui reste, sur la ligne meme : c'est le chiffre
              qu'on additionne en parcourant un rayon. */}
          {showValue ? (
            <span className="block text-caption tabular-nums text-content-muted">
              {line.value_minor === null
                ? t('inventory.noCost')
                : formatMoney({ amount_minor: line.value_minor, currency }, i18n.language)}
            </span>
          ) : null}
        </span>
      </button>

      {isOpen ? (
        <RowDetail
          line={line}
          showValue={showValue}
          eventId={eventId}
          defaultThreshold={defaultThreshold}
        />
      ) : null}
    </Card>
  )
}

function RowDetail({
  line,
  showValue,
  eventId,
  defaultThreshold,
}: {
  line: StockLine
  showValue: boolean
  eventId: string | undefined
  defaultThreshold: number
}) {
  const { t } = useTranslation()
  const record = useRecordMovement(eventId)
  const count = useCount(eventId)
  const threshold = useThreshold(eventId)
  const unitCost = useUnitCost(eventId)
  const movements = useMovements(eventId, line.ref_id)

  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [counted, setCounted] = useState('')

  const busy = record.isPending || count.isPending
  const error = record.isError
    ? record.error.message
    : count.isError
      ? count.error.message
      : threshold.isError
        ? threshold.error.message
        : unitCost.isError
          ? unitCost.error.message
          : null

  const submit = (kind: 'receipt' | 'return' | 'loss') => {
    const value = Number(quantity)
    if (!Number.isFinite(value) || value === 0) return
    record.mutate(
      {
        item_type: line.item_type as ItemKind,
        ref_id: line.ref_id,
        kind,
        quantity: value,
        reason: reason.trim() || null,
      },
      {
        onSuccess: () => {
          setQuantity('')
          setReason('')
        },
      },
    )
  }

  return (
    <div className="mt-4 space-y-5 border-t border-line-subtle pt-4">
      {/* Saisir un mouvement : une quantité, puis le motif d'un seul geste. */}
      <div>
        <p className="overline text-content-muted">{t('inventory.recordTitle')}</p>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="block text-caption text-content-muted">
            {t('inventory.quantity')}
            <input
              type="number"
              inputMode="numeric"
              step={1}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className="mt-1 h-12 w-24 rounded-md border border-line bg-bg-base px-3 text-content-primary tabular-nums"
            />
          </label>
          <label className="block min-w-40 flex-1 text-caption text-content-muted">
            {t('inventory.reason')}
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => submit('receipt')}>
            {t('inventory.kinds.receipt')}
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => submit('loss')}>
            {t('inventory.kinds.loss')}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => submit('return')}>
            {t('inventory.kinds.return')}
          </Button>
        </div>
      </div>

      {/* Le comptage : on tape ce qu'on a compté, le serveur en tire l'écart. */}
      <div>
        <p className="overline text-content-muted">{t('inventory.countTitle')}</p>
        <p className="mt-1 text-caption text-content-muted">{t('inventory.countHint')}</p>
        {/* Ce qui devrait être sur l'étagère : le vendu est parti, le réservé
            est encore là. C'est ce chiffre-là que le comptage confronte. */}
        <p className="mt-1 text-caption text-content-muted">
          {t('inventory.onShelf', { count: line.stock_quantity - line.sold_count })}
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input
            type="number"
            inputMode="numeric"
            step={1}
            min={0}
            value={counted}
            aria-label={t('inventory.countTitle')}
            onChange={(event) => setCounted(event.target.value)}
            className="h-12 w-24 rounded-md border border-line bg-bg-base px-3 text-content-primary tabular-nums"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={busy || counted === ''}
            onClick={() =>
              count.mutate(
                {
                  item_type: line.item_type as ItemKind,
                  ref_id: line.ref_id,
                  counted: Number(counted),
                  reason: null,
                },
                { onSuccess: () => setCounted('') },
              )
            }
          >
            {t('inventory.confirmCount')}
          </Button>
        </div>
      </div>

      {/* Le prix d'achat. Il se saisit aussi au catalogue, mais c'est ici,
          carton et facture en main, qu'on le connaît vraiment. Vide = inconnu,
          ce qui n'est pas zéro : la ligne sort alors du total plutôt que de le
          tirer vers le bas. */}
      {showValue ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="block text-caption text-content-muted">
            {t('inventory.unitCost')}
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              defaultValue={
                line.unit_cost_minor === null ? '' : line.unit_cost_minor / 100
              }
              onBlur={(event) => {
                const raw = event.target.value.trim()
                const next = raw === '' ? null : Math.round(Number(raw) * 100)
                if (next !== null && !Number.isFinite(next)) return
                if (next === line.unit_cost_minor) return
                unitCost.mutate({
                  item_type: line.item_type as ItemKind,
                  ref_id: line.ref_id,
                  unit_cost_minor: next,
                })
              }}
              className="mt-1 h-12 w-28 rounded-md border border-line bg-bg-base px-3 text-content-primary tabular-nums"
            />
          </label>
          <p className="pb-3 text-caption text-content-muted">
            {t('inventory.unitCostHint')}
          </p>
        </div>
      ) : null}

      {/* Le seuil propre au produit. Vide = celui de l'édition. */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-caption text-content-muted">
          {t('inventory.ownThreshold')}
          <input
            type="number"
            inputMode="numeric"
            step={1}
            min={0}
            defaultValue={line.threshold}
            onBlur={(event) => {
              const raw = event.target.value.trim()
              const next = raw === '' ? null : Number(raw)
              if (next === line.threshold) return
              threshold.mutate({
                item_type: line.item_type as ItemKind,
                ref_id: line.ref_id,
                threshold: next,
              })
            }}
            className="mt-1 h-12 w-24 rounded-md border border-line bg-bg-base px-3 text-content-primary tabular-nums"
          />
        </label>
        <p className="pb-3 text-caption text-content-muted">
          {t('inventory.defaultThreshold', { count: defaultThreshold })}
        </p>
      </div>

      {error ? <p className="text-sm text-ember-300">{error}</p> : null}

      {/* L'écart entre le journal et les compteurs. Dit, jamais corrigé seul. */}
      {line.drift !== 0 ? (
        <p className="text-caption text-content-muted">
          {t('inventory.drift', { count: line.drift })}
        </p>
      ) : null}

      <div>
        <p className="overline text-content-muted">{t('inventory.journal')}</p>
        <ul className="mt-2 space-y-1">
          {(movements.data ?? []).map((movement) => (
            <MovementRow key={movement.id} movement={movement} />
          ))}
        </ul>
        {movements.data?.length === 0 ? (
          <p className="mt-2 text-caption text-content-muted">{t('inventory.noMovement')}</p>
        ) : null}
      </div>
    </div>
  )
}

function MovementRow({ movement }: { movement: Movement }) {
  const { t, i18n } = useTranslation()

  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line-subtle py-1.5 last:border-0">
      <span className="min-w-0 text-sm text-content-secondary">
        <span
          className={cn(
            'font-mono tabular-nums',
            movement.quantity > 0 ? 'text-state-success' : 'text-ember-300',
          )}
        >
          {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
        </span>{' '}
        {t(`inventory.kinds.${movement.kind}`)}
        {movement.reason ? (
          <span className="text-content-muted"> — {movement.reason}</span>
        ) : null}
      </span>
      <span className="shrink-0 text-caption text-content-muted">
        {new Date(movement.created_at).toLocaleString(i18n.language, {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
        {movement.author ? ` · ${movement.author}` : ''}
      </span>
    </li>
  )
}
